import {
  AppointmentTime,
  AvailableVeterinarian
} from "../../../Shared/Provet/Models/ProvetCloud/React";
import { AvailableAppointmentTimesResponse } from "./appointmentTimes";

/**
 * Fetches the available times for an interval. Rejects when they can't be loaded.
 */
export type AvailableTimesFetcher = (
  startDate: Date,
  endDate: Date,
  includeFirstAvailableDate: boolean
) => Promise<AvailableAppointmentTimesResponse>;

/**
 * Represents a week in the calendar.
 * @property days - Array of day models for the week.
 */
export interface ProvetWeekModel {
  days: ProvetDayModel[];
}

/**
 * Represents a day in the calendar.
 * @property date - The date for the day.
 * @property availableAppointments - Optional. Array of available appointment times for the day.
 */
export interface ProvetDayModel {
  date: Date;
  availableAppointments?: AppointmentTime[];
}

/**
 * Represents a month in the calendar, including weeks and veterinarians.
 * @property weeks - Array of week models for the month.
 * @property veterinarians - Array of available veterinarians for the month.
 * @property firstDateOfMonth - The first date of the month.
 * @property lastDateOfMonth - The last date of the month.
 * @property bufferTime - Buffer time in minutes for appointments.
 * @property populateMonth - Function to populate the month with available times.
 * @property getAppointmentsForDate - Function to get appointments for a specific date and veterinarian.
 */
export interface ProvetMonthModel {
  weeks: ProvetWeekModel[];
  veterinarians: AvailableVeterinarian[];
  firstDateOfMonth: Date;
  lastDateOfMonth: Date;
  firstAvailableDate?: Date;
  bufferTime: number;
  populateMonth: (
    availableTimes: AvailableTimesFetcher | null,
    includeFirstAvailableDate?: boolean
  ) => Promise<void>;
  getAppointmentsForDate: (date: Date, vetId: number) => AppointmentTime[];
}

/**
 * Helper class to create and manage a ProvetMonthModel.
 */
export class ProvetMonthModelBuilder {
  private weeks: ProvetWeekModel[] = [];
  private veterinarians: AvailableVeterinarian[] = [];
  private firstDateOfMonth: Date;
  private lastDateOfMonth: Date;
  private firstAvailableDate: Date | undefined;
  private bufferTime: number;

  constructor(selectedDate: Date, bufferTime: number = 0) {
    this.bufferTime = bufferTime;

    // Calculate first and last date of month
    this.firstDateOfMonth = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      1
    );

    // C# logic: new DateTime(selectedDate.Year, selectedDate.Month, FirstDateOfMonth.AddMonths(1).AddDays(-1).Day);
    const nextMonth = new Date(this.firstDateOfMonth);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    const lastDayOfMonth = new Date(nextMonth);
    lastDayOfMonth.setDate(lastDayOfMonth.getDate() - 1);
    this.lastDateOfMonth = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      lastDayOfMonth.getDate()
    );
  }

  async populateMonth(
    availableTimes: AvailableTimesFetcher | null,
    includeFirstAvailableDate: boolean = false
  ): Promise<void> {
    let availableTimesForMonth: AppointmentTime[] = [];

    // An empty month is built without asking for times, e.g. to show the month's days when
    // its times failed to load
    if (availableTimes) {
      const now = new Date();
      const startDate = this.firstDateOfMonth < now ? now : this.firstDateOfMonth;

      // Add two extra weeks of days according to how the default Provet Cloud Online Booking is implemented.
      const endDate = new Date(this.lastDateOfMonth);
      endDate.setDate(endDate.getDate() + 14);

      // A failure is passed on to the caller, so it isn't shown as a month without times
      const availableTimesResponse = await availableTimes(
        startDate,
        endDate,
        includeFirstAvailableDate
      );
      availableTimesForMonth = availableTimesResponse.availableTimes;
      this.firstAvailableDate = availableTimesResponse.firstAvailableDate
        ? new Date(availableTimesResponse.firstAvailableDate)
        : undefined;
    }

    // Calculate the first date of the week
    // C# logic: var diff = (7 + (FirstDateOfMonth.DayOfWeek - DayOfWeek.Monday)) % 7;
    const diff = (7 + (this.firstDateOfMonth.getDay() - 1)) % 7; // Monday = 1 in JS (0-6), but we want Monday = 0
    const firstDayInCalendar = new Date(this.firstDateOfMonth);
    firstDayInCalendar.setDate(firstDayInCalendar.getDate() - diff);

    // Calculate and add six full weeks to the calendar
    this.weeks = [];
    for (let weekIndex = 0; weekIndex < 6; weekIndex++) {
      const week: ProvetWeekModel = { days: [] };

      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        // C# logic: var date = firstDayInCalendar.AddDays(dayIndex + (weekIndex * 7));
        const date = new Date(firstDayInCalendar);
        date.setDate(date.getDate() + dayIndex + weekIndex * 7);

        // C# filtering logic: availableAppointment.AvailableTime?.Date.Equals(date)
        const availableAppointments = availableTimesForMonth.filter(
          availableAppointment => {
            const appointmentTime = new Date(availableAppointment.availableTime); // Always string from API
            // Compare just the date part (ignore time)
            const appointmentDate = new Date(
              appointmentTime.getFullYear(),
              appointmentTime.getMonth(),
              appointmentTime.getDate()
            );
            const currentDate = new Date(
              date.getFullYear(),
              date.getMonth(),
              date.getDate()
            );
            return appointmentDate.getTime() === currentDate.getTime();
          }
        );

        week.days.push({
          date: date,
          availableAppointments: availableAppointments
        });

        // Extract veterinarians from available appointments
        if (availableAppointments.length > 0) {
          const vets: AvailableVeterinarian[] = availableAppointments.map(a => ({
            id: a.veterinarianId,
            name: a.veterinarianName,
            title: a.veterinarianTitle,
            type: a.veterinarianType || 0
          }));

          // C# logic: if (!Veterinarians.Any(v => v.Id.Equals(vet.Id)))
          vets.forEach(vet => {
            if (!this.veterinarians.some(v => v.id === vet.id)) {
              this.veterinarians.push(vet);
            }
          });
        }
      }

      this.weeks.push(week);
    }
  }

  getAppointmentsForDate(
    selectedDate: Date,
    selectedVetId: number = 0
  ): AppointmentTime[] {
    // C# logic: Weeks.SelectMany(w => w.Days.Where(date => date.Date.Equals(selectedDate))).FirstOrDefault();
    const day = this.weeks
      .flatMap(w => w.days)
      .find(day => {
        const dayDate = new Date(
          day.date.getFullYear(),
          day.date.getMonth(),
          day.date.getDate()
        );
        const selectedDateOnly = new Date(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate()
        );
        return dayDate.getTime() === selectedDateOnly.getTime();
      });

    if (!day?.availableAppointments) {
      return [];
    }

    // C# logic: Only display available times for the selected day that is after the current hour.
    // var availableTimes = day?.AvailableAppointments?.Where(time => time.AvailableTime.HasValue && time.AvailableTime.Value > DateTime.Now.AddMinutes(BufferTime)).ToList();
    const now = new Date();
    const bufferTimeMs = this.bufferTime * 60 * 1000;
    const cutoffTime = new Date(now.getTime() + bufferTimeMs);

    const availableTimes = day.availableAppointments.filter(time => {
      const appointmentTime = new Date(time.availableTime); // Always string from API
      return appointmentTime > cutoffTime;
    });

    if (selectedVetId > 0) {
      // C# logic: time.VeterinarianId.Equals(selectedVetId) and OrderBy(time => time.AvailableTime!.Value)
      return availableTimes
        .filter(time => time.veterinarianId === selectedVetId)
        .sort((a, b) => {
          const timeA = new Date(a.availableTime); // Always string from API
          const timeB = new Date(b.availableTime); // Always string from API
          return timeA.getTime() - timeB.getTime();
        });
    } else {
      // C# logic: DistinctBy(time => time.AvailableTime!.Value).OrderBy(time => time.AvailableTime!.Value)
      const uniqueTimes = new Map<number, AppointmentTime>();
      availableTimes.forEach(time => {
        const appointmentTime = new Date(time.availableTime); // Always string from API
        const timeKey = appointmentTime.getTime();
        if (!uniqueTimes.has(timeKey)) {
          uniqueTimes.set(timeKey, time);
        }
      });

      return Array.from(uniqueTimes.values()).sort((a, b) => {
        const timeA = new Date(a.availableTime); // Always string from API
        const timeB = new Date(b.availableTime); // Always string from API
        return timeA.getTime() - timeB.getTime();
      });
    }
  }

  build(): ProvetMonthModel {
    return {
      weeks: this.weeks,
      veterinarians: this.veterinarians,
      firstDateOfMonth: this.firstDateOfMonth,
      lastDateOfMonth: this.lastDateOfMonth,
      firstAvailableDate: this.firstAvailableDate,
      bufferTime: this.bufferTime,
      populateMonth: this.populateMonth.bind(this),
      getAppointmentsForDate: this.getAppointmentsForDate.bind(this)
    };
  }
}
